import { useState } from 'react';
import { AritoFormV2, AritoIcon, BottomActionBarV2, DialogV2 } from '@/components/custom/erp';
import { AssetGroup, AssetGroupInput } from '@/types/schemas/nhom-tscd.type';
import { FormSchema, initialFormValues, type FormValues } from '../schemas';
import { useToast } from '@/hooks/use-toast';
import BasicInfo from './BasicInfo';

interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AssetGroupInput) => void;
  formMode: 'add' | 'edit' | 'view';
  initialData?: AssetGroup;
  onAddButtonClick?: () => void;
  onEditButtonClick?: () => void;
  onDeleteButtonClick?: () => void;
  onCopyButtonClick?: () => void;
  assetGroups?: AssetGroup[];
}

const FormDialog = ({
  open,
  onClose,
  onSubmit,
  formMode,
  initialData,
  onAddButtonClick,
  onEditButtonClick,
  onDeleteButtonClick,
  onCopyButtonClick,
  assetGroups = []
}: FormDialogProps) => {
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSubmit = async (data: FormValues) => {
    try {
      // Check duplicate mã nhóm
      const isDuplicate = assetGroups.some(group => {
        // Trong mode edit, bỏ qua bản ghi hiện tại
        if (formMode === 'edit' && initialData && group.uuid === initialData.uuid) {
          return false;
        }
        return group.ma_nhom.toLowerCase() === data.ma_nhom.toLowerCase();
      });

      if (isDuplicate) {
        const errorMessage = `Mã nhóm "${data.ma_nhom}" đã tồn tại. Vui lòng nhập mã khác.`;
        setError(errorMessage);
        return;
      }

      const updatedData: AssetGroupInput = {
        ma_nhom: data.ma_nhom,
        ten_phan_nhom: data.ten_phan_nhom,
        ten2: data.ten2,
        trang_thai: data.trang_thai,
        loai_nhom: data.loai_nhom
      };
      onSubmit(updatedData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Có lỗi xảy ra';
      setError(message);
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  };

  const title = formMode === 'add' ? 'Thêm nhóm TSCĐ' : formMode === 'edit' ? 'Sửa nhóm TSCĐ' : 'Xem nhóm TSCĐ';

  return (
    <DialogV2
      open={open}
      onClose={onClose}
      title={title}
      maxWidth='md'
      disableBackdropClose={true}
      disableEscapeKeyDown={true}
    >
      <AritoFormV2<FormValues>
        mode={formMode}
        hasAritoActionBar={false}
        schema={FormSchema}
        onSubmit={handleSubmit}
        initialData={
          (formMode === 'add' && initialData
            ? {
                ...initialData,
                uuid: undefined, // Xóa uuid để tạo bản ghi mới
                ma_nhom: '' // Xóa mã để người dùng nhập mã mới
              }
            : initialData || initialFormValues) as Partial<FormValues>
        }
        className='w-full md:min-w-[500px] lg:min-w-[600px]'
        headerFields={<BasicInfo formMode={formMode} />}
        bottomBar={
          <BottomActionBarV2
            mode={formMode}
            onAdd={onAddButtonClick}
            onEdit={onEditButtonClick}
            onDelete={onDeleteButtonClick}
            onCopy={onCopyButtonClick}
            onSubmit={() => {}}
            onClose={onClose}
          />
        }
      />
      {error && <div className='mx-4 mb-4 rounded bg-red-100 p-2 text-red-700'>{error}</div>}
    </DialogV2>
  );
};

export default FormDialog;
